import { SITE_URL, type Locale } from "./i18n";
import type { HeroImageSet } from "./hero-images";

/**
 * Single source of truth for robots directives. Every page emits exactly one
 * `<meta name="robots">`: content routes get theirs from `buildHead` (which
 * always emits one, index or noindex), and `HeadContent` de-dupes the root's
 * default away by `name`. Non-`buildHead` routes fall back to the root default.
 * 404 responses use the noindex,nofollow value, resolved in the root `head()`
 * from the matched route status, plus an `X-Robots-Tag` header.
 */
export const ROBOTS_INDEX = "index, follow, max-image-preview:large";
export const ROBOTS_NOINDEX = "noindex, follow";
export const ROBOTS_NOINDEX_NOFOLLOW = "noindex, nofollow";

export const COMPANY = {
  name: "Shugert",
  founded: "2012",
  email: "hello@shugert.com.mx",
  phone: "+1 (332) 256-1212",
  url: SITE_URL,
  sameAs: [
    "https://www.linkedin.com/company/shugert-marketing",
    "https://www.instagram.com/shugertagency/",
    "https://www.facebook.com/shugertagency",
    "https://www.shopify.com/partners/directory/partner/shugert-marketing",
    "https://twitter.com/ShugertMKT",
    "https://shugertmarketing.com/",
    "https://shugert.es/",
  ],
  offices: [
    {
      city: "Hermosillo",
      country: "MX",
      region: "Sonora",
      postal: "83000",
      street: "Hermosillo, Sonora",
    },
    {
      city: "Barcelona",
      country: "ES",
      region: "Barcelona",
      postal: "08019",
      street: "Cristobal de Moura 49",
    },
    { city: "New York", country: "US", region: "NY", postal: "10001", street: "New York, NY" },
  ],
};

export interface PageHeadOptions {
  locale: Locale;
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  type?: "website" | "article";
  altPath: string; // alternate-locale path
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Optional LCP image URL to <link rel="preload" as="image"> for faster LCP. */
  preloadImage?: string;
  preloadImageType?: string;
  /** Responsive preload (matches the same srcset/sizes used on the rendered <img>). */
  preloadImageSrcset?: string;
  preloadImageSizes?: string;
  /** Optional media query for the preload link (e.g. "(min-width: 1024px)"). */
  preloadImageMedia?: string;
  /** Shortcut: preload an AVIF hero from a HeroImageSet. Overrides the manual preloadImage fields. */
  preloadHero?: HeroImageSet;
  /** Override <meta name="author"> for this route (e.g. article author). */
  author?: string;
  /** Optional <link rel="alternate" type="application/rss+xml"> entries. */
  feeds?: Array<{ href: string; title: string }>;
  /** When true, emit <meta name="robots" content="noindex, follow"> for this page. */
  noindex?: boolean;
  /** ISO timestamp, emitted as <meta property="article:published_time"> when type === "article". */
  publishedTime?: string;
  /** ISO timestamp, emitted as <meta property="article:modified_time"> when type === "article". */
  modifiedTime?: string;
  /** Primary topic tag, emitted as <meta property="article:section">. */
  articleSection?: string;
  /** Author name, emitted as <meta property="article:author">. Falls back to `author`. */
  articleAuthor?: string;
  /**
   * Absolute URL or site-relative path that overrides the default self-canonical
   * (`SITE_URL + path`). Use when a page must point its canonical elsewhere.
   */
  canonical?: string;
  /** Overrides <meta property="og:title">. Falls back to `title`. */
  ogTitle?: string;
  /** Overrides <meta property="og:description">. Falls back to `description`. */
  ogDescription?: string;
  /** Overrides <meta name="twitter:title">. Falls back to `ogTitle` then `title`. */
  twitterTitle?: string;
  /** Overrides <meta name="twitter:description">. Falls back to `ogDescription` then `description`. */
  twitterDescription?: string;
}

interface MetaTag {
  title?: string;
  charSet?: string;
  name?: string;
  property?: string;
  content?: string;
}

type LinkTag = {
  rel: string;
  href: string;
  hrefLang?: string;
  type?: string;
  // HTML-standard lowercase attribute names so PageSpeed/Lighthouse and
  // every renderer (including TanStack head) see them on the wire.
  imagesrcset?: string;
  imagesizes?: string;
  // React camelCase prop for the HTML fetchpriority attribute.
  fetchPriority?: "high" | "low" | "auto";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
};

interface ScriptTag {
  type?: string;
  children?: string;
  src?: string;
}

export interface HeadResult {
  meta: MetaTag[];
  links: LinkTag[];
  scripts?: ScriptTag[];
}

const DEFAULT_OG = "/og-default-v2.jpg";

export function buildHead(opts: PageHeadOptions): HeadResult {
  const url = `${SITE_URL}${opts.path}`;
  const canonicalHref = opts.canonical
    ? opts.canonical.startsWith("http")
      ? opts.canonical
      : `${SITE_URL}${opts.canonical}`
    : url;
  const ogImage = opts.ogImage ?? DEFAULT_OG;
  const ogImageAbs = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;
  const ogLocale = opts.locale === "es" ? "es_MX" : "en_US";
  const altLocale = opts.locale === "es" ? "en_US" : "es_MX";
  const enHref = opts.locale === "en" ? `${SITE_URL}${opts.path}` : `${SITE_URL}${opts.altPath}`;
  const esHref = opts.locale === "es" ? `${SITE_URL}${opts.path}` : `${SITE_URL}${opts.altPath}`;

  const ogTitle = opts.ogTitle ?? opts.title;
  const ogDescription = opts.ogDescription ?? opts.description;
  const twitterTitle = opts.twitterTitle ?? ogTitle;
  const twitterDescription = opts.twitterDescription ?? ogDescription;

  const meta: MetaTag[] = [
    { title: opts.title },
    { name: "description", content: opts.description },
    { property: "og:title", content: ogTitle },
    { property: "og:description", content: ogDescription },
    { property: "og:url", content: url },
    { property: "og:type", content: opts.type ?? "website" },
    { property: "og:locale", content: ogLocale },
    { property: "og:locale:alternate", content: altLocale },
    { property: "og:image", content: ogImageAbs },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: twitterTitle },
    { name: "twitter:description", content: twitterDescription },
    { name: "twitter:image", content: ogImageAbs },
  ];

  if (opts.author) {
    meta.push({ name: "author", content: opts.author });
  }

  // Always emit exactly one robots directive. Because `HeadContent` de-dupes
  // meta by `name` (last match wins), this single tag overrides the root's
  // default and no page ends up with two `<meta name="robots">`.
  meta.push({ name: "robots", content: opts.noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX });

  // Article freshness signals (Google + Facebook + LinkedIn use these).
  if ((opts.type ?? "website") === "article") {
    if (opts.publishedTime) {
      meta.push({ property: "article:published_time", content: opts.publishedTime });
    }
    if (opts.modifiedTime) {
      meta.push({ property: "article:modified_time", content: opts.modifiedTime });
    }
    const articleAuthor = opts.articleAuthor ?? opts.author;
    if (articleAuthor) {
      meta.push({ property: "article:author", content: articleAuthor });
    }
    if (opts.articleSection) {
      meta.push({ property: "article:section", content: opts.articleSection });
    }
  }

  // Only emit hreflang pair when a real alternate exists for the other locale.
  // (When enHref === esHref Semrush flags it as a "hreflang conflict".)
  // Use React's camelCase prop names, React serializes `hrefLang` to the
  // lowercase `hreflang` HTML attribute that crawlers expect.
  const links: LinkTag[] = [{ rel: "canonical", href: url }];
  // A page may point its canonical elsewhere (e.g. a syndicated blog post).
  // The default stays self-referencing.
  if (opts.canonical && canonicalHref !== url) links[0].href = canonicalHref;
  if (enHref !== esHref) {
    links.push(
      { rel: "alternate", hrefLang: "en", href: enHref },
      { rel: "alternate", hrefLang: "es", href: esHref },
      { rel: "alternate", hrefLang: "x-default", href: enHref },
    );
  }

  if (opts.preloadImage) {
    links.push({
      rel: "preload",
      as: "image",
      href: opts.preloadImage,
      ...(opts.preloadImageType ? { type: opts.preloadImageType } : {}),
      ...(opts.preloadImageSrcset ? { imagesrcset: opts.preloadImageSrcset } : {}),
      ...(opts.preloadImageSizes ? { imagesizes: opts.preloadImageSizes } : {}),
      ...(opts.preloadImageMedia ? { media: opts.preloadImageMedia } : {}),
      fetchPriority: "high",
    });
  }

  if (opts.preloadHero) {
    const hero = opts.preloadHero;
    links.push({
      rel: "preload",
      as: "image",
      href: hero.avif1x,
      type: "image/avif",
      imagesrcset: `${hero.avif1x} 1x, ${hero.avif2x} 2x`,
      fetchPriority: "high",
    });
  }

  if (opts.feeds) {
    for (const feed of opts.feeds) {
      const href = feed.href.startsWith("http") ? feed.href : `${SITE_URL}${feed.href}`;
      links.push({
        rel: "alternate",
        type: "application/rss+xml",
        title: feed.title,
        href,
      });
    }
  }

  const scripts: ScriptTag[] = [];
  if (opts.jsonLd) {
    const items = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd];
    for (const item of items) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(item),
      });
    }
  }

  return { meta, links, scripts };
}

export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${COMPANY.url}#org`,
    name: COMPANY.name,
    url: COMPANY.url,
    logo: `${COMPANY.url}/brand/shugert-wordmark-black.svg`,
    image: `${COMPANY.url}/og-default-v2.jpg`,
    foundingDate: COMPANY.founded,
    founder: { "@id": `${COMPANY.url}/about/samuel-noriega#person` },
    slogan: "Senior Shopify engineering for established DTC and B2B brands.",
    award: "Shopify Select Partner",
    knowsAbout: [
      "Shopify",
      "Shopify Plus",
      "Shopify Functions",
      "Checkout Extensibility",
      "Hydrogen",
      "Oxygen",
      "Headless Commerce",
      "B2B Commerce",
      "Shopify Markets",
      "Core Web Vitals",
      "Conversion Rate Optimization",
      "Technical SEO",
      "Shopify Migrations",
      "Replatforming",
      "Shopify App Development",
      "ERP Integration",
      "PIM Integration",
      "WMS Integration",
      "Klaviyo",
      "ReCharge",
    ],
    makesOffer: [
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify Performance",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: 1500,
          maxPrice: 3000,
        },
        itemOffered: {
          "@type": "Service",
          name: "Shopify Performance & Core Web Vitals Audit",
          url: `${COMPANY.url}/shopify-performance`,
        },
      },
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify Development",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: 1500,
        },
        itemOffered: {
          "@type": "Service",
          name: "Shopify Development & Replatform",
          url: `${COMPANY.url}/shopify-experts`,
        },
      },
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify CRO",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: 2000,
        },
        itemOffered: {
          "@type": "Service",
          name: "Shopify CRO, Conversion Rate Optimization",
          url: `${COMPANY.url}/shopify-cro`,
        },
      },
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify Technical SEO",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: 2000,
        },
        itemOffered: {
          "@type": "Service",
          name: "Shopify Technical SEO",
          url: `${COMPANY.url}/shopify-technical-seo`,
        },
      },
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify Analytics & AI",
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: 2500,
        },
        itemOffered: {
          "@type": "Service",
          name: "Analytics & AI for Shopify",
          url: `${COMPANY.url}/analytics-ai`,
        },
      },
      {
        "@type": "Offer",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        category: "Shopify Retainer",
        itemOffered: {
          "@type": "Service",
          name: "Shopify Retainer",
          url: `${COMPANY.url}/retainer`,
        },
      },
    ],
    description:
      "Shugert is a Shopify engineering firm supporting established DTC and B2B businesses across North America, Europe and international markets. The company works across storefront performance, conversion, technical SEO, migrations, applications, integrations, Shopify Plus and ongoing delivery.",
    sameAs: COMPANY.sameAs,
    address: COMPANY.offices.map((o) => ({
      "@type": "PostalAddress",
      streetAddress: o.street,
      addressLocality: o.city,
      addressRegion: o.region,
      addressCountry: o.country,
      postalCode: o.postal,
    })),
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: COMPANY.email,
        telephone: COMPANY.phone,
        areaServed: ["US", "MX", "ES"],
        availableLanguage: ["English", "Spanish"],
      },
    ],
  };
}

export function professionalServiceJsonLd(locale: Locale) {
  // LocalBusiness represents a single physical location. Emit only the
  // primary (headquarters) address; the multi-office list lives on
  // Organization.address.
  const hq = COMPANY.offices[0];
  return {
    "@context": "https://schema.org",
    "@type": ["ProfessionalService", "LocalBusiness"],
    "@id": `${COMPANY.url}/#localbusiness`,
    name: `${COMPANY.name}, Shopify Select Partner`,
    image: `${COMPANY.url}/og-default-v2.jpg`,
    logo: `${COMPANY.url}/brand/shugert-wordmark-black.svg`,
    url: COMPANY.url,
    telephone: COMPANY.phone,
    email: COMPANY.email,
    description:
      locale === "es"
        ? "Shopify Select Partner. Diseño, desarrollo, performance y crecimiento para tiendas Shopify y Shopify Plus de marcas DTC establecidas en EE. UU. y Latam."
        : "Shopify Select Partner agency. Design, development, performance, and growth for Shopify and Shopify Plus stores of established DTC brands across the USA and Latam.",
    priceRange: "$$$",
    areaServed: ["United States", "Mexico", "Spain", "Latin America"],
    address: {
      "@type": "PostalAddress",
      streetAddress: hq.street,
      addressLocality: hq.city,
      addressRegion: hq.region,
      addressCountry: hq.country,
      postalCode: hq.postal,
    },
    sameAs: COMPANY.sameAs,
    parentOrganization: { "@id": `${COMPANY.url}#org` },
  };
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}

export interface HowToStepInput {
  name: string;
  text: string;
}

export function howToJsonLd(name: string, description: string, steps: HowToStepInput[]) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name,
    description,
    step: steps.map((s, idx) => ({
      "@type": "HowToStep",
      position: idx + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

export function websiteJsonLd(locale: Locale = "en") {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${COMPANY.url}#website`,
    name: COMPANY.name,
    url: COMPANY.url,
    // The site publishes both locales; declaring only one mislabels every
    // page of the other locale.
    inLanguage: locale === "es" ? ["es-MX", "en-US"] : ["en-US", "es-MX"],
    // A bare @id reference to the Organization node defined once in
    // organizationJsonLd(), not a re-declared object — restating type/name/url
    // here would create a second, divergent node under the same @id, which is
    // what schema.org validators flag as a conflicting duplicate.
    publisher: { "@id": `${COMPANY.url}#org` },
    // Sitelinks Search Box removed: the site has no live on-site search
    // endpoint, so exposing a SearchAction template caused crawlers (and
    // GSC) to attempt to fetch literal `?q={search_term_string}` URLs.
  };
}

export interface WebPageJsonLdInput {
  name: string;
  path: string;
  description: string;
  inLanguage: "es-MX" | "en-US";
  dateModified?: string;
}

/**
 * Per-page WebPage JSON-LD. Use on the top ~10 indexable routes to give
 * Google + LLM crawlers an explicit page-level entity tied to the
 * sitewide WebSite + Organization defined in __root.tsx.
 */
export function webPageJsonLd(input: WebPageJsonLdInput) {
  const url = `${COMPANY.url}${input.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: input.name,
    url,
    description: input.description,
    inLanguage: input.inLanguage,
    dateModified: input.dateModified ?? new Date().toISOString(),
    // Bare @id references to the WebSite/Organization nodes declared once
    // sitewide (in websiteJsonLd()/organizationJsonLd()). Restating them as
    // full objects here duplicated the same @id with a different property
    // set on every page, which is what the schema.org validator errors
    // flagged sitewide.
    isPartOf: { "@id": `${COMPANY.url}#website` },
    publisher: { "@id": `${COMPANY.url}#org` },
  };
}

export interface ServiceSchemaInput {
  name: string;
  description: string;
  path: string;
  serviceType: string;
  minPrice?: number;
  maxPrice?: number;
  unit?: "PROJECT" | "MONTH" | "HOUR";
  deliverables?: string[];
  locale?: Locale;
}

export function serviceJsonLd(input: ServiceSchemaInput) {
  const offers: Record<string, unknown> = {
    "@type": "Offer",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url: `${COMPANY.url}${input.path}`,
  };
  if (input.minPrice !== undefined) {
    const ps: Record<string, unknown> = {
      "@type": "PriceSpecification",
      priceCurrency: "USD",
      minPrice: input.minPrice,
    };
    if (input.maxPrice !== undefined) ps.maxPrice = input.maxPrice;
    if (input.unit) ps.unitText = input.unit;
    offers.priceSpecification = ps;
  }
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: input.name,
    description: input.description,
    serviceType: input.serviceType,
    url: `${COMPANY.url}${input.path}`,
    inLanguage: input.locale === "es" ? "es" : "en",
    provider: { "@id": `${COMPANY.url}#org` },
    areaServed: ["United States", "Mexico", "Spain", "Latin America"],
    offers,
    ...(input.deliverables && input.deliverables.length > 0
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${input.name} deliverables`,
            itemListElement: input.deliverables.map((d) => ({
              "@type": "Offer",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              itemOffered: { "@type": "Service", name: d },
            })),
          },
        }
      : {}),
  };
}
